import React, { useEffect, useState, useContext } from 'react'
import { Helmet } from 'react-helmet'
import { Container } from 'react-bootstrap'
import { ABSpinner, useUserService, ModalProvider } from '@abroad/components'
import { SignupForm } from '../components'
import AuthService from '../utils/AuthService'
import { InviteDetailsContext } from '../utils/contexts'
import API from '../utils/API'
import {
  checkUserGetOldPlan,
  convertOldPlanWithNewPlan,
  getExpiresDate,
} from '../utils/utility'
import { sowRefId as sowRefIdConst } from '../utils/constants'
import QuestEntryModal from './QuestEntryModal'

const Signup = ({ location: { search }, history }) => {
  const [isLoading, setIsLoading] = useState(false)
  const { inviteLinkDetails } = useContext(InviteDetailsContext)
  const query = new URLSearchParams(search)
  const { user } = useUserService()
  const sowRefId = query.get(sowRefIdConst)
  const mode = query.get('mode')
  const fName = query.get('fname')
  const code = query.get('e') || query.get('c')
  const isQuestEntry = Boolean(
    code &&
      inviteLinkDetails &&
      (inviteLinkDetails?.welcomeLetter || inviteLinkDetails?.title),
  )
  const [showModal, setShowModal] = useState(false)
  const authenticatedRedirectPath = user?.isQuestParticipated
    ? '/your-experiences'
    : '/home'

  useEffect(() => {
    if (isQuestEntry) {
      const hasSeenModal = localStorage.getItem('hasSeenQuestEntryModal')
      if (!hasSeenModal) {
        setShowModal(true)
        localStorage.setItem('hasSeenQuestEntryModal', 'true')
      }
    }

    // Add event listener for tab/site close
    const handleBeforeUnload = () => {
      localStorage.removeItem('hasSeenQuestEntryModal')
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    // Cleanup event listener
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isQuestEntry])

  useEffect(() => {
    if (!sowRefId && AuthService.isLogedIn()) {
      history.replace(authenticatedRedirectPath)
      return
    }
    if (!sowRefId) return
    localStorage.setItem(sowRefIdConst, sowRefId)
    if (user?._id && !user?.emailVerified) {
      history.replace(`/verify-email-sent`)
    }
    if (AuthService.isLogedIn()) {
      history.replace(
        `/plans-payments/coaching-plan/${sowRefId}?${sowRefIdConst}=${sowRefId}`,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sowRefId, authenticatedRedirectPath, user, history])

  useEffect(() => {
    if (search) {
      const expires = getExpiresDate(7) // 7 days after expired
      const query = new URLSearchParams(search)
      const m = query.get('mode')
      if (!(m === 'invite')) {
        document.cookie = `${process.env.REACT_APP_COOKIE_PREFIX}_search=${search};expires=${expires};path=/`
      }
      const queryPlanCode = query.get('plan') || query.get('planCode')
      if (queryPlanCode) {
        // replace url & setPlan with new plan value
        const isOldPlanMatch = checkUserGetOldPlan(queryPlanCode)
        if (isOldPlanMatch) {
          const newPlanCode = convertOldPlanWithNewPlan(queryPlanCode)
          if (query.has('plan')) {
            query.set('plan', newPlanCode)
          } else {
            query.set('planCode', newPlanCode)
          }
          if (!AuthService.isLogedIn()) {
            history.replace({ search: query.toString() })
          }
        }
        const needPlanUpgrade = async () => {
          if (query.has('promo')) {
            query.set('promocode', query.get('promo'))
            query.delete('promo')
          }
          if (query.has('planCode')) {
            query.set('plan', query.get('planCode'))
            query.delete('planCode')
          }
          try {
            const { data } = await API.user.needPlanUpgrade(
              `?newPlan=${query.get('plan') || query.get('planCode')}`,
            )
            if (data?.needUpgrade) {
              history.replace(`/user-payment?${query.toString()}`)
            } else {
              history.replace('/home')
            }
          } catch (e) {
            history.replace('/home')
          }
        }
        if (AuthService.isLogedIn()) {
          setIsLoading(true)
          needPlanUpgrade()
        }
      }
    } else {
      if (AuthService.isLogedIn()) {
        history.replace(authenticatedRedirectPath)
      }
    }
  }, [search, history, authenticatedRedirectPath, user])

  if (isLoading) {
    return <ABSpinner />
  }

  return (
    <>
      <Helmet>
        <title>{'Login to Abroad Platform'}</title>
        <meta name='title' content={'Login to Abroad Platform'}></meta>
        <meta property='og:title' content={'Login to Abroad Platform'}></meta>
        <meta
          property='twitter:title'
          content={'Login to Abroad Platform'}></meta>
        <link
          rel='canonical'
          href={`${process.env.REACT_APP_DOMAIN}/account`}></link>
        <meta
          property='og:url'
          content={`${process.env.REACT_APP_DOMAIN}/account`}></meta>
        <meta
          property='twitter:url'
          content={`${process.env.REACT_APP_DOMAIN}/account`}></meta>
      </Helmet>
      <main>
        <ModalProvider>
          <Container className='px-0' fluid>
            {isQuestEntry && (
              <QuestEntryModal
                mode={mode}
                firstName={fName}
                show={showModal}
                onClose={() => setShowModal(false)}
              />
            )}
            <SignupForm
              isShowAbroadLogo={!isLoading}
              isQuestEntry={isQuestEntry}
            />
          </Container>
        </ModalProvider>
      </main>
    </>
  )
}

export default Signup
